/**
 * Regnereglene for utfordringer, samlet ett sted slik at foreldre- og
 * barnevisningen alltid viser samme tall og samme rekkefølge.
 *
 * Utfordringer teller STJERNER, ikke antall registreringer. En oppgave som er
 * verdt to stjerner teller altså dobbelt av en som er verdt én - ellers ville
 * en lang oppgave og en kjapp én vært like mye verdt i konkurransen. Antallet
 * ganger vises fortsatt ved siden av, som bakgrunn for tallet.
 *
 * Både mål og premie er valgfrie:
 *   'none'      ingen måltall - rangeres på stjerner
 *   'shared'    ett felles stjernetall hele familien bidrar mot
 *   'per_child' hvert barn sitt eget tall; rangeres på hvor langt hver er
 *               kommet mot SITT mål, slik at alder betyr mindre
 */

import { t } from './i18n'

const todayIso = () => {
  const d = new Date()
  // Lokal dato, ikke UTC - ellers bytter utfordringer status ved midnatt UTC.
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function challengeStatus(challenge) {
  const today = todayIso()
  if (today < challenge.starts_on) return 'upcoming'
  if (today > challenge.ends_on) return 'finished'
  return 'active'
}

/**
 * Ordet som står på merket foran en utfordring.
 *
 * Tekstene må stå som literaler inni t() for at `npm run i18n` skal finne
 * dem: skriptet leser koden, ikke kjøretiden. Lå de i en oppslagstabell og ble
 * oversatt som t(TABELL[status]), fantes «Pågår» ikke som literal noe sted -
 * og merket ble stående på norsk uansett hvilket språk familien hadde valgt.
 */
export function statusLabel(status) {
  if (status === 'upcoming') return t('Starter snart')
  if (status === 'finished') return t('Ferdig')
  return t('Pågår')
}

/** Hele dager igjen, talt fra i dag til og med sluttdatoen. */
export function daysLeft(challenge) {
  const end = new Date(`${challenge.ends_on}T23:59:59`)
  return Math.max(0, Math.ceil((end - new Date()) / 86_400_000))
}

export function daysUntilStart(challenge) {
  const start = new Date(`${challenge.starts_on}T00:00:00`)
  return Math.max(0, Math.ceil((start - new Date()) / 86_400_000))
}

/**
 * Setter sammen én utfordring med deltakere, stilling og premier.
 * `data` er rådataene fra api.loadChallengeData().
 */
export function composeChallenge(challenge, data, memberById) {
  const taskIds = data.tasks
    .filter((t) => t.challenge_id === challenge.id)
    .map((t) => t.task_id)

  const rows = data.standings.filter((s) => s.challenge_id === challenge.id)

  const participants = data.participants
    .filter((p) => p.challenge_id === challenge.id)
    .map((p) => {
      const row = rows.find((s) => s.child_id === p.child_id)
      const stars = row?.stars ?? 0
      const target = challenge.goal_type === 'per_child' ? (p.goal_target || null) : null

      return {
        child_id: p.child_id,
        child: memberById(p.child_id),
        goal_target: target,
        stars,
        completions: row?.completions ?? 0,
        earned_ore: Number(row?.earned_ore ?? 0),
        // Andel av eget mål. Null når barnet ikke har et mål å måles mot.
        ratio: target ? stars / target : null,
        prize: data.prizes.find(
          (pz) => pz.challenge_id === challenge.id && pz.child_id === p.child_id) ?? null,
      }
    })

  // Rangering: har hvert barn sitt eget mål, teller andelen av det målet -
  // ellers rå stjerner. Stjerner brukes uansett til å bryte likt resultat.
  const sorted = [...participants].sort((a, b) => {
    if (challenge.goal_type === 'per_child' && a.ratio !== null && b.ratio !== null) {
      if (b.ratio !== a.ratio) return b.ratio - a.ratio
    }
    return b.stars - a.stars
  })

  const totalStars = participants.reduce((sum, p) => sum + p.stars, 0)
  const totalCompletions = participants.reduce((sum, p) => sum + p.completions, 0)
  const totalEarnedOre = participants.reduce((sum, p) => sum + p.earned_ore, 0)
  const best = sorted[0]

  // Ingen leder før noen faktisk har gjort noe. Flere kan lede likt.
  const leaders = !best || best.stars === 0
    ? []
    : sorted.filter((p) =>
      challenge.goal_type === 'per_child' && best.ratio !== null
        ? p.ratio === best.ratio
        : p.stars === best.stars)

  const sharedRatio = challenge.goal_type === 'shared' && challenge.goal_target
    ? totalStars / challenge.goal_target
    : null

  return {
    ...challenge,
    status: challengeStatus(challenge),
    taskIds,
    participants: sorted,
    totalStars,
    totalCompletions,
    totalEarnedOre,
    leaders,
    leaderIds: leaders.map((p) => p.child_id),
    sharedRatio,
    goalReached:
      challenge.goal_type === 'shared' ? (sharedRatio ?? 0) >= 1
        : challenge.goal_type === 'per_child'
          ? participants.length > 0 && participants.every((p) => (p.ratio ?? 0) >= 1)
          : false,
    hasPrize: Number(challenge.prize_ore) > 0,
    prizePaid: data.prizes.some((pz) => pz.challenge_id === challenge.id),
  }
}

/** Sorterer utfordringer slik en familie vil se dem: pågående først. */
export function sortChallenges(list) {
  const order = { active: 0, upcoming: 1, finished: 2 }
  return [...list].sort((a, b) =>
    order[a.status] - order[b.status] ||
    (a.status === 'finished' ? b.ends_on.localeCompare(a.ends_on) : a.ends_on.localeCompare(b.ends_on)))
}
